import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties, ComputedRef, Ref } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { Subscription, SubscriptionAutoRenewTone } from '@entities/subscription'

import { Status as CustomerStatus } from '@brighterly/lib-core-types/customer/enums'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { useCustomerStore } from '@entities/customer'
import { SubscriptionModal } from '@entities/subscription'
import { useListsStore } from '@shared/model'
import { AppRoute, TasksKanbanRelatedToType } from '@shared/config'

import { useSubscriptionsStore } from './store'
import { useSubscriptionsApi } from '../api/list.hook'
import { useSubscriptionSync } from '../api/sync.hook'
import { getSubscriptionChangelogUrl } from './subscription-links'
import { getAutoRenewTone, getChargeNowHint } from './subscription-options'
import {
  canApplyDiscount,
  canCancel,
  canChangePause,
  canChangeProduct,
  canChargeNow,
  canPause,
  canResume,
  isChargeNowAvailable,
  isProductChangeLocked,
} from './subscription-rules'

interface UseSubscriptionActionsOptions {
  subscription: ComputedRef<Subscription>
  canManage: ComputedRef<boolean>
  /** Legacy offered the task items only beside the customer's own page: its plan card and its tab's details modal. */
  withTasks: ComputedRef<boolean>
}

interface SubscriptionActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  isProcessing: ComputedRef<boolean>
  isChangeProductVisible: ComputedRef<boolean>
  isChangeProductDisabled: ComputedRef<boolean>
  isSplitVisible: ComputedRef<boolean>
  isDiscountVisible: ComputedRef<boolean>
  isChargeNowVisible: ComputedRef<boolean>
  isChargeNowDisabled: ComputedRef<boolean>
  chargeNowHint: ComputedRef<string | undefined>
  isResumeVisible: ComputedRef<boolean>
  isChangePauseVisible: ComputedRef<boolean>
  isPauseVisible: ComputedRef<boolean>
  isPremiumTemplateVisible: ComputedRef<boolean>
  isCancelVisible: ComputedRef<boolean>
  isTasksVisible: ComputedRef<boolean>
  autoRenewText: ComputedRef<string>
  autoRenewColor: ComputedRef<SubscriptionAutoRenewTone>
  toggle: () => void
  openModal: (modal: SubscriptionModal) => void
  openSplit: () => void
  openChangelog: () => Promise<void>
  createTask: () => Promise<void>
  openRelatedTasks: () => Promise<void>
  syncSubscription: () => Promise<void>
}

export const useSubscriptionActions = ({
  subscription,
  canManage,
  withTasks,
}: UseSubscriptionActionsOptions): SubscriptionActionsReturn => {
  const store = useSubscriptionsStore()
  const customerStore = useCustomerStore()
  const listsStore = useListsStore()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const isProcessing = computed<boolean>(() => store.isProcessing)

  const isManageable = (rule: (target: Subscription) => boolean): ComputedRef<boolean> =>
    computed<boolean>(() => canManage.value && rule(subscription.value))

  const isChangeProductVisible = isManageable(canChangeProduct)
  const isChangeProductDisabled = computed<boolean>(() => isProcessing.value || isProductChangeLocked(subscription.value))
  const isSplitVisible = isManageable(target => !!target.customer)
  const isDiscountVisible = isManageable(canApplyDiscount)
  const isChargeNowVisible = isManageable(canChargeNow)
  const isChargeNowDisabled = computed<boolean>(() => !isChargeNowAvailable(subscription.value))
  const chargeNowHint = computed<string | undefined>(() => getChargeNowHint(subscription.value))
  const isResumeVisible = isManageable(canResume)
  const isChangePauseVisible = isManageable(canChangePause)
  const isPauseVisible = isManageable(canPause)
  const isPremiumTemplateVisible = isManageable(target => target.upsell_can_be_sent)
  const isCancelVisible = isManageable(canCancel)
  const isTasksVisible = computed<boolean>(() => withTasks.value
    && !!listsStore.getLists?.tasks_kanban?.enabled
    && customerStore.getCustomer?.status === CustomerStatus.Deal)

  const autoRenewText = computed<string>(() =>
    (subscription.value.auto_renew ? 'Disable auto-renew' : 'Enable auto-renew'))
  const autoRenewColor = computed<SubscriptionAutoRenewTone>(() => getAutoRenewTone(!subscription.value.auto_renew))

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const openModal = (modal: SubscriptionModal): void => {
    isOpen.value = false
    store.openModal(modal, subscription.value.id)
  }

  const openSplit = (): void => {
    openModal(SubscriptionModal.Split)
  }

  const openChangelog = async (): Promise<void> => {
    isOpen.value = false
    await navigateTo(getSubscriptionChangelogUrl(subscription.value.id))
  }

  const createTask = async (): Promise<void> => {
    const customerId = customerStore.getCustomer?.id

    if (!customerId) return

    isOpen.value = false
    await navigateTo({
      path: AppRoute.TasksKanbanCreation,
      query: {
        customer_id: String(customerId),
        related_to_id: String(subscription.value.id),
        related_to_type: TasksKanbanRelatedToType.Subscription,
      },
    })
  }

  const openRelatedTasks = async (): Promise<void> => {
    isOpen.value = false
    await navigateTo({
      path: AppRoute.TasksKanbanSupport,
      query: {
        related_to_id: String(subscription.value.id),
        related_to_type: TasksKanbanRelatedToType.Subscription,
      },
    })
  }

  const syncSubscription = async (): Promise<void> => {
    isOpen.value = false
    // Opened from the details modal, the sync replaces it like every other action does.
    store.closeModal()

    const synced = await useSubscriptionSync(subscription.value.id)

    if (synced) {
      await useSubscriptionsApi()
    }
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    isProcessing,
    isChangeProductVisible,
    isChangeProductDisabled,
    isSplitVisible,
    isDiscountVisible,
    isChargeNowVisible,
    isChargeNowDisabled,
    chargeNowHint,
    isResumeVisible,
    isChangePauseVisible,
    isPauseVisible,
    isPremiumTemplateVisible,
    isCancelVisible,
    isTasksVisible,
    autoRenewText,
    autoRenewColor,
    toggle,
    openModal,
    openSplit,
    openChangelog,
    createTask,
    openRelatedTasks,
    syncSubscription,
  }
}
